import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { AppError } from "@/jenesys-types/errors";

import { CompletionMeta, RequestMeta } from "@/store/store";

interface FileMetadata {
  name: string;
  size: number;
  type: string;
}

interface DocumentIngestState {
  isUploading: boolean;
  uploadSuccess: boolean;
  uploadError?: AppError;
}

const initialState: DocumentIngestState = {
  isUploading: false,
  uploadSuccess: false,
  uploadError: undefined
};

interface UploadDocumentPayload {
  files: Array<FileMetadata>;
}

export type UploadDocumentActions = PayloadAction<
  UploadDocumentPayload,
  string,
  RequestMeta
>;

export type UploadDocumentSuccessAction = PayloadAction<
  UploadDocumentPayload,
  string,
  CompletionMeta | undefined
>;

interface UploadDocumentFailurePayload {
  error?: AppError;
}

export type UploadDocumentFailureAction =
  PayloadAction<UploadDocumentFailurePayload>;

const documentIngestSlice = createSlice({
  name: "features/documentsUpload",
  initialState,
  reducers: {
    uploadDocuments: {
      reducer: (state: DocumentIngestState, _action: UploadDocumentActions) => {
        state.isUploading = true;
        state.uploadSuccess = false;
        state.uploadError = undefined;
      },
      prepare: (files: Array<File>, redirectTo: string) => ({
        payload: {
          files: files.map((file) => ({
            name: file.name,
            size: file.size,
            type: file.type
          }))
        },
        meta: {
          onSuccess: { redirectTo, message: "Documents uploaded successfully" }
        }
      })
    },
    uploadDocumentSuccess: {
      reducer: (
        state: DocumentIngestState,
        _action: UploadDocumentSuccessAction
      ) => {
        state.isUploading = false;
        state.uploadSuccess = true;
        state.uploadError = undefined;
      },
      prepare: (files: Array<FileMetadata>, meta?: CompletionMeta) => ({
        payload: { files },
        meta
      })
    },
    uploadDocumentsFailure: {
      reducer: (
        state: DocumentIngestState,
        action: UploadDocumentFailureAction
      ) => {
        state.isUploading = false;
        state.uploadSuccess = false;
        state.uploadError = action.payload.error;
      },
      prepare: (error?: AppError) => ({ payload: { error } })
    }
  }
});

export const {
  uploadDocuments,
  uploadDocumentSuccess,
  uploadDocumentsFailure
} = documentIngestSlice.actions;

const selectDocumentIngestState = (state: {
  features: { DocumentIngest: DocumentIngestState };
}): DocumentIngestState => state.features.DocumentIngest;

export const selectIsUploading = createSelector(
  selectDocumentIngestState,
  (state) => state.isUploading
);
export const selectUploadSuccess = createSelector(
  selectDocumentIngestState,
  (state) => state.uploadSuccess
);

export default documentIngestSlice.reducer;
